import { TripLegType } from '@/data/models/reservation.model';
import type { AnyActorRef } from 'xstate';

import {
  emptyReservationFormValues,
  emptyTripSegment,
  type ReservationFormInput,
  type ReservationFormValues,
} from '../schema/reservationForm.schema';
import { getTripsFromMachines } from './getTripsFromMachines';

import type { ReservationFormMainContext } from '../machines/reservationFormMain.types';
import type { TripTypeContext } from '../machines/tripType.machine';

export function pickSecondaryAttributeFieldsFromForm(
  live: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(live)) {
    if (key.startsWith('SecondaryAttribute_')) {
      out[key] = live[key];
    }
  }
  return out;
}

function readTripTypeField(ref: AnyActorRef | null): TripLegType {
  if (!ref) return TripLegType.OneWay;
  const ctx = ref.getSnapshot().context as TripTypeContext;
  return ctx.value;
}

export function getAggregatedFormValues(
  context: ReservationFormMainContext,
): ReservationFormValues {
  return {
    ...emptyReservationFormValues,
    tripType: readTripTypeField(context.tripTypeRef),
    trips: getTripsFromMachines(context),
  } as ReservationFormValues;
}

export function mergeTripsWithLive(
  machineTrips: ReservationFormValues['trips'],
  liveTrips: ReservationFormInput['trips'] | undefined,
  tripType: ReservationFormValues['tripType'],
): ReservationFormValues['trips'] {
  const live = liveTrips ?? [];
  // One-way and round-trip always use a single leg in RHF. While machines resize
  // (SET_TRIP_COUNT runs in a layout effect), machineTrips can still be longer.
  const n =
    tripType === TripLegType.MultiLeg
      ? Math.max(machineTrips.length, live.length, 1)
      : 1;
  return Array.from({ length: n }, (_, i) => {
    const machine = machineTrips[i] ?? {
      ...emptyTripSegment,
      tripLegType: tripType,
    };
    const liveTrip = live[i] ?? {};
    const merged = { ...machine, ...liveTrip };
    // Live RHF trips can carry empty pickupNotes/dropoffNotes when the trip
    // tab is unmounted; don't let those wipe notes the field machines still hold.
    if (!String(merged.pickupNotes ?? '').trim() && machine.pickupNotes) {
      merged.pickupNotes = machine.pickupNotes;
    }
    if (!String(merged.dropoffNotes ?? '').trim() && machine.dropoffNotes) {
      merged.dropoffNotes = machine.dropoffNotes;
    }
    const locKeys = [
      'AttributeLevel1',
      'AttributeLevel2',
      'AttributeLevel3',
      'AttributeLevel4',
      'AttributeLevel5',
      'AttributeLevel6',
      'MobilityAttribute',
      'passengerMobility',
      'passengerAssistance',
      'height',
      'weight',
    ] as const;
    for (const key of locKeys) {
      if (!String(merged[key] ?? '').trim() && machine[key]) {
        merged[key] = machine[key];
      }
    }
    return merged;
  }) as ReservationFormValues['trips'];
}

/** Merges machine-derived trips/tripType with live RHF top-level fields (phones, override, errors). */
export function mergeAggregatedWithLiveForm(
  context: ReservationFormMainContext,
  live: Partial<ReservationFormInput>,
): ReservationFormValues {
  const agg = getAggregatedFormValues(context);
  const tripType = live.tripType ?? agg.tripType;
  return {
    ...agg,
    tripType,
    genericErrors: live.genericErrors ?? agg.genericErrors,
    MemberHomePhone: live.MemberHomePhone ?? agg.MemberHomePhone,
    MemberMobilePhone: live.MemberMobilePhone ?? agg.MemberMobilePhone,
    AllowText: live.AllowText ?? agg.AllowText,
    ShowOtherInputs: live.ShowOtherInputs ?? agg.ShowOtherInputs,
    AlternateContact: live.AlternateContact ?? agg.AlternateContact,
    AlternateContactType: live.AlternateContactType ?? agg.AlternateContactType,
    AlternateContactFirstName:
      live.AlternateContactFirstName ?? agg.AlternateContactFirstName,
    AlternateContactLastName:
      live.AlternateContactLastName ?? agg.AlternateContactLastName,
    AlternateContactPhone:
      live.AlternateContactPhone ?? agg.AlternateContactPhone,
    AlternateContactAllowSMS:
      live.AlternateContactAllowSMS ?? agg.AlternateContactAllowSMS,
    AdditionalPassengers: live.AdditionalPassengers ?? agg.AdditionalPassengers,
    AdditionalPassengerCount:
      live.AdditionalPassengerCount ?? agg.AdditionalPassengerCount,
    AllowOverride: live.AllowOverride ?? agg.AllowOverride,
    OverrideFirstName: live.OverrideFirstName ?? agg.OverrideFirstName,
    OverrideLastName: live.OverrideLastName ?? agg.OverrideLastName,
    OverrideReason: live.OverrideReason ?? agg.OverrideReason,
    OverrideServicePlan: live.OverrideServicePlan ?? agg.OverrideServicePlan,
    RequestedProvider: live.RequestedProvider ?? agg.RequestedProvider,
    RequestedTransportationProviderID:
      live.RequestedTransportationProviderID ??
      agg.RequestedTransportationProviderID,
    PassengerNoteTypes: live.PassengerNoteTypes ?? agg.PassengerNoteTypes,
    DriverNoteTypes: live.DriverNoteTypes ?? agg.DriverNoteTypes,
    trips: mergeTripsWithLive(agg.trips, live.trips, tripType),
    ...pickSecondaryAttributeFieldsFromForm(live as Record<string, unknown>),
  } as ReservationFormValues;
}
